import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { Reveal } from "@/components/reveal";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const TITLE = "How to start a creator storefront";
const DESCRIPTION =
  "A step-by-step guide to launching a creator storefront. Compare the Amazon Influencer Program with the Pluggz editorial model and choose the right path for your audience.";
const PATH = "/journal/how-to-start-a-creator-storefront";

export const Route = createFileRoute("/journal_/how-to-start-a-creator-storefront")({
  head: () => ({
    meta: [
      { title: `${TITLE} — Pluggz` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Pluggz" },
          publisher: {
            "@type": "Organization",
            name: "Pluggz",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
          },
          mainEntityOfPage: `${SITE_URL}${PATH}`,
          datePublished: "2026-06-11",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
            { "@type": "ListItem", position: 3, name: TITLE, item: `${SITE_URL}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <article className="relative">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-accent/20 blur-3xl float-slow" />

      <nav aria-label="Breadcrumb" className="relative mx-auto max-w-3xl px-6 pt-10">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <li><Link to="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li><Link to="/journal" className="hover:text-foreground transition-colors">Journal</Link></li>
          <li aria-hidden><ChevronRight className="h-3 w-3" /></li>
          <li aria-current="page" className="text-foreground">Start a storefront</li>
        </ol>
      </nav>

      <header className="relative mx-auto max-w-3xl px-6 pt-10 pb-10 text-center">
        <Reveal>
          <p className="editorial-eyebrow inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Guide · 10 min read
          </p>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="mt-4 font-serif text-4xl leading-tight md:text-6xl">
            How to start a creator <span className="italic shimmer">storefront</span>
          </h1>
        </Reveal>
        <Reveal delay={260}>
          <p className="mt-6 text-sm text-muted-foreground">By the Pluggz Editorial Team · June 2026</p>
        </Reveal>
      </header>

      <Reveal>
        <div className="relative mx-auto mb-12 max-w-5xl px-6">
          <div className="aspect-[16/7] w-full overflow-hidden rounded-3xl bg-gradient-to-br from-accent/30 via-card to-background ring-1 ring-border/60" />
        </div>
      </Reveal>

      <div className="mx-auto max-w-2xl px-6 pb-24 font-light text-foreground/90">
        <section className="prose-spacing space-y-6 leading-relaxed">
          <p className="text-lg">
            If you have ever searched for <em>how to create an Amazon storefront</em> or <em>how to start a creator storefront</em>, you are not alone. Thousands of creators ask the same question every month. The good news is that launching a storefront has never been easier. The harder part is choosing the right model for your brand, your audience, and the kind of income you want to build. This guide walks you through both paths — the Amazon Influencer Program and the editorial storefront model — so you can pick the one that fits.
          </p>

          <h2 className="mt-12 font-serif text-3xl">What a creator storefront actually is</h2>
          <p className="text-muted-foreground">
            A creator storefront is a shoppable page curated by an individual rather than a retailer. Instead of browsing thousands of SKUs, a visitor sees a short, opinionated list of products the creator actually uses and recommends. The trust is personal. The conversion is higher. And the economics are different from traditional affiliate marketing because the shopper is not just buying a product — they are buying into a point of view.
          </p>
          <p className="text-muted-foreground">
            There are two dominant ways to build one today. The first is platform-native: Amazon's Influencer Program lets approved creators build an "Idea List" storefront inside Amazon's ecosystem. The second is independent: tools like Pluggz let creators build an editorial storefront on their own domain, with hand-vetted products, direct brand relationships, and a checkout experience that lives outside any single marketplace.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The Amazon Influencer Program: how it works</h2>
          <p className="text-muted-foreground">
            Amazon's program is the path most creators know. Once accepted, you get a vanity URL — amazon.com/shop/yourname — and the ability to create lists of Amazon products. You earn commissions on qualifying purchases, typically between one and ten percent depending on the category. The setup is fast, the catalog is enormous, and the trust Amazon has already built with shoppers lowers the friction to buy.
          </p>
          <p className="text-muted-foreground">
            The tradeoffs are well documented. Your storefront lives on Amazon's domain, so you do not own the traffic or the email list. The commission rates are fixed and have declined over time. Your product selection is limited to what Amazon stocks, which means you cannot recommend small direct-to-consumer brands that your audience might love more. And because every product page looks identical, your storefront competes on price, not on taste.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The editorial model: taste as the product</h2>
          <p className="text-muted-foreground">
            The alternative is to treat your storefront as a publication, not a catalog. An editorial storefront is built around a simple idea: your audience follows you because they like how you think, not because they want to comparison-shop. Every product on the page is there because you chose it. The photography, the copy, and the layout all reinforce your aesthetic. The result is a destination that feels like a magazine issue rather than a vending machine.
          </p>
          <p className="text-muted-foreground">
            On Pluggz, this model gets two structural advantages. First, products are hand-vetted by the editorial team, so creators do not waste time filtering out dropshipped junk. Second, checkout happens directly with the brand, which means higher average commissions, faster fulfillment for the shopper, and a relationship between creator and brand that can grow into long-term partnerships. The shopper buys from the brand, not from a marketplace — and the brand knows you sent them.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Step-by-step: starting your first storefront</h2>
          <p className="text-muted-foreground">
            <strong>1. Define your editorial lens.</strong> Before you add a single product, write one sentence that explains what this storefront is about. "Skincare that actually works for sensitive skin" is a lens. "Things I like" is not. The lens becomes your filter for every product decision that follows.
          </p>
          <p className="text-muted-foreground">
            <strong>2. Pick your first ten products.</strong> Ten great products will outperform fifty adequate ones. For each item, write a single line that justifies its place: how long you have used it, what problem it solved, what it replaced. Specificity builds trust faster than enthusiasm.
          </p>
          <p className="text-muted-foreground">
            <strong>3. Group by occasion, not by aisle.</strong> "Morning routine" sells better than "cleansers." Occasion-led collections answer a question the shopper is already asking. Build three to five collections and rotate one in every month.
          </p>
          <p className="text-muted-foreground">
            <strong>4. Shorten the path to checkout.</strong> Every redirect between a product tap and the cart costs you roughly fifteen percent of intent. If a shopper has to leave your page, visit an intermediary, and then hunt for the product on a brand site, you have already lost them. Direct brand checkout — product to cart in two taps — is the biggest conversion lever you have.
          </p>
          <p className="text-muted-foreground">
            <strong>5. Publish updates like a magazine.</strong> A storefront that has not changed in six weeks reads as abandoned. Add a "this month I am loving" section. Swap in a new collection. Write a short note about a brand you just discovered. Returning visitors should see your taste move.
          </p>

          <h2 className="mt-12 font-serif text-3xl">Amazon vs. editorial: which is right for you?</h2>
          <p className="text-muted-foreground">
            Choose the Amazon Influencer Program if you want the fastest possible start, you already have an audience that shops heavily on Amazon, and you are comfortable trading control for convenience. It is a sensible on-ramp. But if you want to own your audience relationship, work with brands directly, and build a storefront that reflects your taste rather than Amazon's catalog, the editorial model is the long-term play.
          </p>
          <p className="text-muted-foreground">
            The creators who earn the most over time are usually the ones who treat their storefront as a media property. They invest in photography, write real copy, and refresh their collections regularly. The platform matters less than the editorial discipline. Whether you start on Amazon or on an independent storefront, the habits are the same: curate tightly, justify every pick, and shorten the distance between inspiration and purchase.
          </p>

          <h2 className="mt-12 font-serif text-3xl">The bottom line</h2>
          <p className="text-muted-foreground">
            Starting a creator storefront is not about finding the right tool. It is about committing to a point of view. Pick your lens, choose your first ten products, and publish. The creators who wait for the perfect platform or the perfect audience size rarely launch at all. The ones who ship a tight edit and iterate monthly are the ones who build sustainable income — on Amazon, on Pluggz, or on whatever platform comes next.
          </p>

          <div className="mt-14 rounded-3xl border border-border/60 bg-gradient-to-br from-card via-background to-card p-8 text-center">
            <p className="editorial-eyebrow">Keep reading</p>
            <h3 className="mt-3 font-serif text-2xl">More from the Journal</h3>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              <Link to="/journal" className="group inline-flex items-center gap-2 border-b border-foreground pb-1 text-sm tracking-wide">
                Back to the Journal
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}
